import * as React from "react";
import { cn } from "../lib/cn";
import { AndoLogo } from "./AndoLogo";

export type MessageBubbleVariant = "user" | "agent";

export interface MessageBubbleProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Bubble role:
   *   • `agent` → transparent prose pinned left, brand logo bullet on the
   *     side. Maps to `.msg` / `.msg-body` in the v18 mockup.
   *   • `user` → orange gradient pill anchored right, asymmetric border
   *     radius. Maps to `.msg-user-bubble` (lines 685-696).
   */
  variant: MessageBubbleVariant;
  /** Show the brand mark to the left of agent bubbles. Default `true`. */
  showAvatar?: boolean;
  /** Bubble content (text / markdown body / typing indicator). */
  children: React.ReactNode;
  /** Slot rendered below the bubble (actions + timestamp row, citations, …). */
  footer?: React.ReactNode;
  /** Slot rendered above the bubble (MessageMeta). */
  header?: React.ReactNode;
}

/**
 * MessageBubble — visual shell for a single chat message.
 *
 * Two visual modes, both mirrored from `tasks/mockups/ando_v18.html`:
 *   • agent  → no fill, prose hugs left margin, brand mark beside meta.
 *   • user   → aurora-orange gradient pill, asymmetric 20/20/6/20 radius.
 *
 * Animation `animate-msg-in` is applied at the wrapper so each new bubble
 * eases up 8px on mount (set `data-no-animate` to skip).
 */
export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(
  ({ variant, showAvatar = true, header, footer, children, className, ...rest }, ref) => {
    const isUser = variant === "user";
    return (
      <div
        ref={ref}
        data-message-role={variant}
        className={cn(
          // Responsive cap: bubble grows with the chat pane up to a
          // readable maximum. ``min(860px, 90%)`` keeps roomy chat
          // (focus / 62% split) from collapsing into a 460px column with
          // a sea of whitespace, while preserving line length ≤~95ch.
          // The 90% floor leaves visual breathing room from the pane
          // edges; the 860px ceiling caps line length so prose stays
          // comfortable to read.
          "group/message relative flex max-w-[min(860px,90%)] flex-col gap-1.5",
          "animate-msg-in data-[no-animate]:animate-none",
          isUser ? "ml-auto items-end text-right" : "items-start",
          className,
        )}
        {...rest}
      >
        {header ? <div className="w-full">{header}</div> : null}
        <div className={cn("relative flex w-full gap-2", isUser ? "justify-end" : "justify-start")}>
          {!isUser && showAvatar ? (
            <div className="-mt-0.5 shrink-0">
              <AndoLogo size={18} aria-hidden />
            </div>
          ) : null}
          {isUser ? (
            <div
              className={cn(
                // ``break-words``: a pasted URL or token is a single unbreakable
                // word — without it the glyphs paint straight past the pill's
                // rounded edge instead of wrapping inside it.
                "inline-block max-w-full break-words rounded-[20px_20px_6px_20px] bg-gradient-to-br from-ando-orange via-ando-orange-light to-ando-orange px-4 py-2.5 text-left text-sm text-white leading-[1.5] shadow-sm",
              )}
              style={{ backgroundSize: "200% 200%" }}
            >
              {children}
            </div>
          ) : (
            <div className="min-w-0 flex-1">{children}</div>
          )}
        </div>
        {footer ? <div className="w-full">{footer}</div> : null}
      </div>
    );
  },
);
MessageBubble.displayName = "MessageBubble";
